import { Fragment, useEffect, useState } from 'react';
import {
  getPushNotificationLogs,
  type PushNotificationLog,
} from '../../services/firebase';

interface PushHistoryProps {
  /** Change this value to force a reload (e.g. after a successful send). */
  reloadKey?: number;
}

/**
 * 'ios' → 'iOS'. 'unknown' is the server's bucket for tokens written before the
 * client set a platform; it is shown rather than hidden so the rows still sum
 * to the totals beside them.
 */
function platformDisplayName(platform: string): string {
  if (platform === 'ios') return 'iOS';
  if (platform === 'android') return 'Android';
  if (platform === 'unknown') return 'Unknown';
  return platform;
}

function formatSentAt(log: PushNotificationLog): string {
  const date = log.sentAt?.toDate?.();
  if (!date) return '—';
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function targetSummary(log: PushNotificationLog): string {
  if (log.target === 'all') return 'All';
  const userIds = log.target?.userIds ?? [];
  return `${userIds.length} user${userIds.length === 1 ? '' : 's'}`;
}

export function PushHistory({ reloadKey = 0 }: PushHistoryProps) {
  const [logs, setLogs] = useState<PushNotificationLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    getPushNotificationLogs(20)
      .then((rows) => {
        if (!cancelled) setLogs(rows);
      })
      .catch((err) => {
        console.error('Failed to load push logs:', err);
        if (!cancelled)
          setError(
            err instanceof Error ? err.message : 'Failed to load history',
          );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [reloadKey]);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-900">Recent Sends</h2>
      </div>

      {loading ? (
        <div className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-brand-600" />
        </div>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-700">
          {error}
        </div>
      ) : logs.length === 0 ? (
        <div className="text-center py-8 text-gray-500 bg-white shadow rounded-lg">
          No push notifications sent yet.
        </div>
      ) : (
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Sent
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Sender
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Title
                  </th>
                  <th className="hidden md:table-cell px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Body
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Target
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Delivered / Failed
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {logs.map((log) => {
                  const isExpanded = expanded === log.id;
                  return (
                    <Fragment key={log.id}>
                      <tr
                        onClick={() =>
                          setExpanded(isExpanded ? null : log.id)
                        }
                        className="cursor-pointer hover:bg-gray-50"
                      >
                        <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">
                          {formatSentAt(log)}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700">
                          {log.sentByEmail || log.sentBy || '—'}
                        </td>
                        <td className="px-4 py-3 text-sm font-medium text-gray-900">
                          {log.title}
                        </td>
                        <td className="hidden md:table-cell px-4 py-3 text-sm text-gray-500 max-w-xs truncate">
                          {log.body}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">
                          {targetSummary(log)}
                        </td>
                        <td className="px-4 py-3 text-sm whitespace-nowrap">
                          <span className="text-green-700">
                            {log.fcmSuccessCount}
                          </span>
                          <span className="text-gray-400"> / </span>
                          <span
                            className={
                              log.fcmFailureCount > 0
                                ? 'text-red-700'
                                : 'text-gray-500'
                            }
                          >
                            {log.fcmFailureCount}
                          </span>
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr className="bg-gray-50">
                          <td colSpan={6} className="px-4 py-3">
                            <div className="space-y-2 text-sm">
                              <div>
                                <span className="text-gray-500">Body: </span>
                                <span className="text-gray-900 whitespace-pre-wrap">
                                  {log.body}
                                </span>
                              </div>
                              <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-gray-600">
                                <span>
                                  Recipients: {log.recipientUserCount}
                                </span>
                                <span>Notifications: {log.notificationsWritten}</span>
                                <span>Pruned: {log.prunedTokenCount}</span>
                                {/* The number the totals hide: a send reading
                                    "5 recipients, 1 delivered" looks like a
                                    delivery fault when it may just be that most
                                    of them have no registered device. */}
                                {log.recipientsWithNoDevice !== undefined && (
                                  <span
                                    className={
                                      log.recipientsWithNoDevice > 0
                                        ? 'text-amber-700'
                                        : undefined
                                    }
                                    title="Recipients who got the in-app notification but no push, because they have no registered device."
                                  >
                                    No device: {log.recipientsWithNoDevice}
                                  </span>
                                )}
                                <span className="font-mono">id: {log.id}</span>
                              </div>

                              {/* Per-platform outcome. Only sends made after the
                                  breakdown shipped have one — and an absent
                                  breakdown is NOT zero, so say "not recorded"
                                  rather than rendering a row of zeroes that
                                  would read as "no iOS failures". */}
                              {log.platformBreakdown === undefined ? (
                                <div className="text-xs text-gray-400">
                                  Per-platform breakdown not recorded for this
                                  send.
                                </div>
                              ) : (
                                <div className="text-xs">
                                  <div className="text-gray-500 mb-1">
                                    By platform
                                  </div>
                                  <table className="text-xs">
                                    <thead>
                                      <tr className="text-gray-500">
                                        <th className="text-left pr-4 font-normal">
                                          Platform
                                        </th>
                                        <th className="text-right pr-4 font-normal">
                                          Delivered
                                        </th>
                                        <th className="text-right pr-4 font-normal">
                                          Failed
                                        </th>
                                        <th className="text-right font-normal">
                                          Pruned
                                        </th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {Object.entries(log.platformBreakdown)
                                        .sort(([a], [b]) => a.localeCompare(b))
                                        .map(([platform, counts]) => (
                                          <tr key={platform}>
                                            <td className="pr-4 text-gray-700">
                                              {platformDisplayName(platform)}
                                            </td>
                                            <td className="pr-4 text-right text-green-700">
                                              {counts.success}
                                            </td>
                                            <td
                                              className={`pr-4 text-right ${
                                                counts.failure > 0
                                                  ? 'text-red-700'
                                                  : 'text-gray-500'
                                              }`}
                                            >
                                              {counts.failure}
                                            </td>
                                            <td
                                              className={`text-right ${
                                                counts.pruned > 0
                                                  ? 'text-amber-700'
                                                  : 'text-gray-500'
                                              }`}
                                            >
                                              {counts.pruned}
                                            </td>
                                          </tr>
                                        ))}
                                    </tbody>
                                  </table>
                                </div>
                              )}
                              {log.target !== 'all' && (
                                <div className="text-xs text-gray-600">
                                  <span className="text-gray-500">
                                    Target users:{' '}
                                  </span>
                                  <span className="font-mono">
                                    {log.target.userIds.join(', ')}
                                  </span>
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
